/*
 * Auto-add the "Y" product of a Buy X get Y discount.
 *
 * The discount itself only makes Y free once Y is in the cart -- it never adds
 * it. Which Y belongs to which X comes from the product metafields
 * makedo.auto_add_variant (variant reference) and, optionally,
 * makedo.auto_add_max (integer, e.g. the discount's "max uses per order").
 * snippets/cart-items.liquid publishes every cart line + those values as JSON
 * in [data-auto-add-data]; this script keeps the Y lines (tagged with the
 * hidden line item property _auto_add) in sync with the X lines:
 *   Y qty >= sum of X qty (capped by auto_add_max). Extra Y on top of that
 *   (customer pressed "+") is kept on the SAME line, so Shopify only splits it
 *   into a free part and a full-price part instead of scattering more lines.
 *   When X qty drops by k, the gift qty drops by k too.
 * It reacts to every cart refresh event, so it covers PDP, quick-buy, bundle,
 * drawer qty changes and removals alike.
 */
;(function () {
  var SECTIONS =
    'cart-items,cart-footer,cart-item-count,cart-live-region,free_shipping_progressbar'
  var PROP = '_auto_add'

  var busy = false
  var again = false
  var failedSignature = null

  var STORE_KEY = 'auto-add-gift:wanted'

  function loadWanted() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {}
    } catch (e) {
      return {}
    }
  }

  function saveWanted(map) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(map))
    } catch (e) {}
  }

  function rememberWanted(prevWanted, wanted) {
    var now = {}
    Object.keys(prevWanted).forEach(function (id) {
      now[id] = 0
    })
    Object.keys(wanted).forEach(function (id) {
      now[id] = wanted[id]
    })
    saveWanted(now)
  }

  function readLines(root) {
    var el = root && root.querySelector('[data-auto-add-data]')
    if (!el) return []
    try {
      return JSON.parse(el.textContent) || []
    } catch (e) {
      return []
    }
  }

  function linesFromHTML(html) {
    if (!html) return []
    return readLines(new DOMParser().parseFromString(html, 'text/html'))
  }

  // Works out what needs to change so the gift lines match the X lines.
  function plan(lines, prevWanted) {
    var wanted = {}
    var caps = {}

    lines.forEach(function (l) {
      if (l.auto || !l.y) return
      wanted[l.y] = (wanted[l.y] || 0) + l.qty
      if (l.max > 0) caps[l.y] = caps[l.y] ? Math.min(caps[l.y], l.max) : l.max
    })

    Object.keys(wanted).forEach(function (y) {
      if (caps[y]) wanted[y] = Math.min(wanted[y], caps[y])
    })

    var updates = {}
    var adds = []

    // Shopify may split one gift variant into several lines (e.g. the part
    // covered by the discount and the part beyond its limit), so compare the
    // TOTAL gift qty per variant rather than each line, otherwise we would
    // fight the split forever.
    var giftLines = {}
    lines.forEach(function (l) {
      if (l.auto) (giftLines[l.id] = giftLines[l.id] || []).push(l)
    })

    Object.keys(giftLines).forEach(function (id) {
      var group = giftLines[id]
      var total = group.reduce(function (sum, l) {
        return sum + l.qty
      }, 0)
      var w = wanted[id] || 0
      var excess = 0

      if (total < w) {
        updates[group[0].key] = group[0].qty + (w - total)
      } else if (total > w) {
        // Above the wanted qty is fine (customer added extra Y). Only trim
        // when X went down since we last looked: drop gift qty by the same k.
        var k = prevWanted[id] > w ? prevWanted[id] - w : 0
        excess = total - Math.max(w, total - k)
      }

      for (var i = group.length - 1; i >= 0 && excess > 0; i--) {
        var cut = Math.min(group[i].qty, excess)
        updates[group[i].key] = group[i].qty - cut
        excess -= cut
      }
    })

    Object.keys(wanted).forEach(function (y) {
      if (!giftLines[y] && wanted[y] > 0) {
        var item = { id: Number(y), quantity: wanted[y], properties: {} }
        item.properties[PROP] = 'true'
        adds.push(item)
      }
    })

    return { updates: updates, adds: adds, wanted: wanted }
  }

  function post(url, payload) {
    var config = window.fetchConfigDefaults('javascript')
    config.body = JSON.stringify(
      Object.assign(payload, {
        sections: SECTIONS,
        sections_url: window.location.pathname,
      })
    )
    return (window.__nativeFetch || window.fetch)(url, config).then(
      function (res) {
        return res.json()
      }
    )
  }

  // Event payloads can be stale when quantities are changed in quick
  // succession, and planning from stale data can add Y twice. Always plan from
  // the cart as it is right now.
  function fetchFreshLines() {
    return (window.__nativeFetch || window.fetch)(
      window.location.pathname + '?sections=cart-items',
      { credentials: 'same-origin', cache: 'no-store' }
    )
      .then(function (res) {
        return res.json()
      })
      .then(function (data) {
        return linesFromHTML(data['cart-items'])
      })
  }

  function run() {
    return fetchFreshLines().then(sync)
  }

  function sync(lines) {
    var prevWanted = loadWanted()
    var p = plan(lines, prevWanted)
    rememberWanted(prevWanted, p.wanted)
    var hasUpdates = Object.keys(p.updates).length > 0

    if (!hasUpdates && !p.adds.length) {
      failedSignature = null
      return Promise.resolve()
    }

    var signature = JSON.stringify({ u: p.updates, a: p.adds })
    if (signature === failedSignature) return Promise.resolve()

    var last
    var chain = Promise.resolve()

    if (hasUpdates) {
      chain = chain.then(function () {
        return post(window.theme.routes.cart_update_url, {
          updates: p.updates,
        }).then(function (data) {
          if (data.status || data.errors) throw data
          last = data
        })
      })
    }

    if (p.adds.length) {
      chain = chain.then(function () {
        return post(window.theme.routes.cart_add_url, { items: p.adds }).then(
          function (data) {
            if (data.status) throw data
            last = data
          }
        )
      })
    }

    return chain
      .then(function () {
        failedSignature = null
        if (!last || !last.sections) return
        if (typeof window.smoothFreeShippingBar === 'function') {
          window.smoothFreeShippingBar(last.sections['free_shipping_progressbar'])
        }
        // cartqtychange refreshes drawer/footer/count without re-opening the drawer
        document.body.dispatchEvent(
          new CustomEvent('shapes:modalcart:cartqtychange', {
            bubbles: true,
            detail: { response: last },
          })
        )
      })
      .catch(function (err) {
        // e.g. Y is sold out -- don't retry the same failing state in a loop
        failedSignature = signature
        console.warn('[auto-add-gift] could not sync gift item', err)
      })
  }

  function schedule() {
    if (busy) {
      again = true
      return
    }
    busy = true
    run()
      .catch(function (err) {
        console.warn('[auto-add-gift] could not read cart', err)
      })
      .then(function () {
        busy = false
        if (again) {
          again = false
          schedule()
        }
      })
  }

  // Called by the drawer right before it changes the qty of line `changedKey`.
  // Returns extra {lineKey: qty} updates for the gift lines so the drawer can
  // send them in the SAME /cart/update request as the X qty change. Changing
  // X first and Y afterwards makes Shopify momentarily show X split in two
  // (discount only partly applied) until Y catches up.
  window.autoAddGiftUpdates = function (changedKey, newQty) {
    var lines = null
    var roots = document.querySelectorAll('[data-auto-add-data]')
    for (var i = 0; i < roots.length && !lines; i++) {
      try {
        var parsed = JSON.parse(roots[i].textContent) || []
        if (
          parsed.some(function (l) {
            return l.key === changedKey
          })
        )
          lines = parsed
      } catch (e) {}
    }
    if (!lines) return null

    var changed = lines.filter(function (l) {
      return l.key === changedKey
    })[0]
    if (changed.auto) return null

    var prevWanted = loadWanted()
    var p = plan(
      lines.map(function (l) {
        return l.key === changedKey ? Object.assign({}, l, { qty: newQty }) : l
      }),
      prevWanted
    )
    rememberWanted(prevWanted, p.wanted)

    return Object.keys(p.updates).length ? p.updates : null
  }

  function onCartRefresh() {
    schedule()
  }

  window.addEventListener('shapes:modalcart:afteradditem', onCartRefresh)
  window.addEventListener('shapes:modalcart:cartqtychange', onCartRefresh)

  document.addEventListener('DOMContentLoaded', function () {
    if (document.querySelector('[data-auto-add-data]')) schedule()
  })
})()
